/**
 * Lightweight page-heading registry for accessible SPA navigation.
 *
 * Pages register their primary heading via `registerPageHeading()`.
 * The router afterEach hook calls `focusRegisteredPageHeading()` to
 * move focus after a route change, giving keyboard and screen-reader
 * users a reliable orientation point.
 */

let currentHeading: HTMLElement | null = null;

/**
 * Register an element as the current page heading.
 * Replaces any previously registered heading.
 */
function registerPageHeading(el: HTMLElement | null): void {
  currentHeading = el;
}

/**
 * Focus the registered page heading.
 * Returns true if a heading was focused, false otherwise.
 */
function focusRegisteredPageHeading(options?: { preventScroll?: boolean }): boolean {
  if (!currentHeading || !currentHeading.isConnected) {
    return false;
  }
  currentHeading.focus(options);
  return document.activeElement === currentHeading;
}

/**
 * Clear the registered heading.
 * If `el` is provided, only clears if it matches the current heading
 * (safe for component unmount where another page may have already registered).
 */
function clearRegisteredPageHeading(el?: HTMLElement | null): void {
  if (el !== undefined && el !== currentHeading) {
    return;
  }
  currentHeading = null;
}

export { registerPageHeading, focusRegisteredPageHeading, clearRegisteredPageHeading };
